import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Message, Spin, Switch, Space, Typography } from '@arco-design/web-react';
import { useTranslation } from 'react-i18next';
import { useOnboardingStatus } from './hooks/useOnboardingData';
import OnboardingLayout from './OnboardingLayout';
import CorporateIdentityStep from './steps/CorporateIdentityStep';
import CorporateDetailsStep from './steps/CorporateDetailsStep';
import CorporateOperatorStep from './steps/CorporateOperatorStep';
import ReviewStep from './steps/ReviewStep';
import './OnboardingWizard.css';

const STEP_KEYS = [
  { titleKey: 'wizard.corporateSteps.representativeData', descKey: 'wizard.corporateSteps.representativeDataDesc' },
  { titleKey: 'wizard.corporateSteps.corporateData', descKey: 'wizard.corporateSteps.corporateDataDesc' },
  { titleKey: 'wizard.corporateSteps.programOperator', descKey: 'wizard.corporateSteps.programOperatorDesc' },
  { titleKey: 'wizard.corporateSteps.review', descKey: 'wizard.corporateSteps.reviewDesc' }
];

function CorporateOnboardingWizard({ previewMode = false }) {
  const { t } = useTranslation('onboarding');

  const STEPS = useMemo(() => STEP_KEYS.map(({ titleKey, descKey }) => ({
    title: t(titleKey),
    description: t(descKey)
  })), [t]);

  const [currentStep, setCurrentStep] = useState(1);
  const [freeMode, setFreeMode] = useState(previewMode);
  const initialStepSet = useRef(false);

  const {
    data: statusPayload,
    isLoading,
    refetch
  } = useOnboardingStatus({ enabled: !previewMode });

  const data = useMemo(() => ({
    identity: statusPayload?.identity_data || null,
    corporateDetails: statusPayload?.role_specific_data || null,
    documents: statusPayload?.documents || null,
    roleName: statusPayload?.role_name || null
  }), [statusPayload]);

  const requestedChanges = useMemo(() =>
    Array.isArray(statusPayload?.requested_changes)
      ? statusPayload.requested_changes
      : [],
  [statusPayload]);

  // Determine initial step from loaded data (runs only once)
  useEffect(() => {
    if (previewMode || !statusPayload || initialStepSet.current) return;
    initialStepSet.current = true;

    const identity = statusPayload.identity_data || {};
    const corporate = statusPayload.role_specific_data || {};

    const isIdentityComplete = [
      'first_name',
      'last_name',
      'date_of_birth',
      'government_id',
      'government_id_type',
      'phone',
      'contact_email'
    ].every((field) => identity[field]);

    if (identity.government_id_type === 'OTHER' && !identity.government_id_type_other) {
      setCurrentStep(1);
      return;
    }

    const hasCorporate = !!corporate.company_legal_name
      && !!corporate.rfc
      && !!corporate.fiscal_address
      && !!corporate.fiscal_city
      && !!corporate.fiscal_state
      && !!corporate.fiscal_postal_code
      && !!corporate.fiscal_country;

    const hasOperator = Array.isArray(corporate.operator_contacts)
      && corporate.operator_contacts.length > 0
      && corporate.operator_contacts.every((contact) => (
        contact.full_name && contact.email && contact.phone
      ));

    if (!isIdentityComplete) {
      setCurrentStep(1);
    } else if (!hasCorporate) {
      setCurrentStep(2);
    } else if (!hasOperator) {
      setCurrentStep(3);
    } else {
      setCurrentStep(4);
    }
  }, [statusPayload, previewMode]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const handleNext = async () => {
    if (previewMode) {
      if (currentStep < STEPS.length) {
        setCurrentStep((prev) => prev + 1);
        window.scrollTo(0, 0);
      }
      return;
    }

    await refetch();

    if (currentStep < STEPS.length) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = async (targetStep) => {
    if (previewMode) {
      if (typeof targetStep === 'number') {
        setCurrentStep(targetStep);
      } else {
        setCurrentStep((prev) => Math.max(1, prev - 1));
      }
      window.scrollTo(0, 0);
      return;
    }

    await refetch();

    if (typeof targetStep === 'number') {
      setCurrentStep(targetStep);
    } else {
      setCurrentStep((prev) => Math.max(1, prev - 1));
    }
    window.scrollTo(0, 0);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CorporateIdentityStep
            key={`corp-identity-${JSON.stringify(data.identity)?.substring(0, 50)}`}
            onNext={handleNext}
            initialData={data.identity}
            documents={data.documents}
            requestedChanges={requestedChanges}
            previewMode={previewMode}
            freeMode={freeMode}
          />
        );
      case 2:
        return (
          <CorporateDetailsStep
            key={`corp-details-${JSON.stringify(data.corporateDetails)?.substring(0, 50)}`}
            onNext={handleNext}
            onBack={handleBack}
            initialData={data.corporateDetails}
            documents={data.documents}
            requestedChanges={requestedChanges}
            previewMode={previewMode}
            freeMode={freeMode}
          />
        );
      case 3:
        return (
          <CorporateOperatorStep
            key={`corp-operator-${JSON.stringify(data.corporateDetails)?.substring(0, 50)}`}
            onNext={handleNext}
            onBack={handleBack}
            initialData={data.corporateDetails}
            requestedChanges={requestedChanges}
            previewMode={previewMode}
            freeMode={freeMode}
          />
        );
      case 4:
        return (
          <ReviewStep
            key={`review-${JSON.stringify(data)?.substring(0, 50)}`}
            onBack={handleBack}
            data={{
              identity: data.identity,
              teacherDetails: data.corporateDetails,
              documents: data.documents,
              roleName: data.roleName
            }}
            requestedChanges={requestedChanges}
            previewMode={previewMode}
            freeMode={freeMode}
            roleVariant="corporate"
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <OnboardingLayout>
        <div className="onboarding-loading">
          <Spin size={40} />
          <p>{t('wizard.loading')}</p>
        </div>
      </OnboardingLayout>
    );
  }

  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex);
    window.scrollTo(0, 0);
  };

  return (
    <OnboardingLayout
      currentStep={currentStep}
      totalSteps={STEPS.length}
      stepTitle={STEPS[currentStep - 1]?.title || ''}
      onStepClick={handleStepClick}
      steps={STEPS}
    >
      <div className="onboarding-wizard">
        <div className="wizard-content">
          {previewMode && (
            <div style={{ marginBottom: 16 }}>
              <Message
                type="warning"
                content={t('wizard.simulationMode')}
                style={{ marginBottom: 12 }}
              />
              <Space align="center">
                <Switch checked={freeMode} onChange={setFreeMode} />
                <Typography.Text>{t('wizard.freeMode')}</Typography.Text>
              </Space>
            </div>
          )}
          {renderStep()}
        </div>
      </div>
    </OnboardingLayout>
  );
}

export default CorporateOnboardingWizard;
